import { EDITOR } from "@/constants/testIds";
import TemplatePicker from "@/components/TemplatePicker";
import ImageUploadField from "@/components/ImageUploadField";
import AiImproveButton from "@/components/AiImproveButton";
import { googleMapsUrl } from "@/lib/wedding";

// Toggle switch inline reutilizable — mismo estilo que Autobús/IBAN.
function SectionToggle({ label, checked, onChange, testId }) {
    return (
        <div className="flex items-center justify-between pb-4 mb-4 border-b border-[var(--line)]">
            <span className="text-sm font-medium">{label}</span>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                data-testid={testId}
                onClick={onChange}
                className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200"
                style={{ background: checked ? "var(--ink)" : "var(--line)" }}
            >
                <span
                    className="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200"
                    style={{
                        transform: checked
                            ? "translateX(22px)"
                            : "translateX(4px)",
                    }}
                />
            </button>
        </div>
    );
}

// Exportación nombrada exacta para que coincida con tu Editor.jsx original
export function getEditorSections(data, update, errors = {}, clearError = () => {}) {
    const updateField = (key) => (e) => {
        update({ ...data, [key]: e.target.value });
        if (errors[key]) clearError(key);
    };

    const updateVenue = (venueKey) => (field) => (e) => {
        const v = { ...data[venueKey], [field]: e.target.value };
        if (field === "address" && !data[venueKey]?.map_url_manual) {
            v.map_url = googleMapsUrl(e.target.value);
        }
        update({ ...data, [venueKey]: v });
    };

    const errCls = (k) => (errors[k] ? "input-soft input-error" : "input-soft");
    const lblCls = (k) => (errors[k] ? "label-soft label-error" : "label-soft");

    return [
        {
            id: "template",
            title: "Plantilla",
            subtitle: "Elige el estilo de tu web",
            render: () => (
                <TemplatePicker
                    value={data.template}
                    onChange={(t) => update({ ...data, template: t })}
                />
            ),
        },
        {
            id: "couple",
            title: "La pareja",
            subtitle: "Vuestros nombres y un titular",
            render: () => (
                <div className="space-y-5">
                    <div>
                        <label className={lblCls("primary_name")}>
                            Primer nombre {errors.primary_name && "*"}
                        </label>
                        <input
                            data-testid={EDITOR.fieldPrimary}
                            type="text"
                            className={errCls("primary_name")}
                            value={data.primary_name}
                            onChange={updateField("primary_name")}
                            placeholder="Ej. Lucía"
                        />
                        {errors.primary_name && (
                            <p className="hint-error">Este campo es obligatorio.</p>
                        )}
                    </div>
                    <div>
                        <label className={lblCls("secondary_name")}>
                            Segundo nombre {errors.secondary_name && "*"}
                        </label>
                        <input
                            data-testid={EDITOR.fieldSecondary}
                            type="text"
                            className={errCls("secondary_name")}
                            value={data.secondary_name}
                            onChange={updateField("secondary_name")}
                            placeholder="Ej. Mateo"
                        />
                        {errors.secondary_name && (
                            <p className="hint-error">Este campo es obligatorio.</p>
                        )}
                    </div>
                    <div>
                        <label className="label-soft">Titular</label>
                        <input
                            data-testid={EDITOR.fieldTagline}
                            type="text"
                            className="input-soft"
                            value={data.tagline}
                            onChange={updateField("tagline")}
                            placeholder="Nos casamos"
                        />
                    </div>
                </div>
            ),
        },
        {
            id: "date",
            title: "Fecha & portada",
            subtitle: "Cuándo y la imagen principal",
            render: () => (
                <div className="space-y-5">
                    <SectionToggle
                        label="Mostrar cuenta atrás"
                        checked={data.countdownEnabled ?? true}
                        onChange={() =>
                            update({
                                ...data,
                                countdownEnabled: !(data.countdownEnabled ?? true),
                            })
                        }
                        testId="editor-toggle-countdown"
                    />
                    <div>
                        <label className={lblCls("wedding_date")}>
                            Fecha y hora {errors.wedding_date && "*"}
                        </label>
                        <input
                            data-testid={EDITOR.fieldDate}
                            type="datetime-local"
                            className={errCls("wedding_date")}
                            value={data.wedding_date}
                            onChange={updateField("wedding_date")}
                        />
                        {errors.wedding_date && (
                            <p className="hint-error">Indica cuándo será.</p>
                        )}
                    </div>
                    <div>
                        <ImageUploadField
                            testId={EDITOR.fieldCoverUpload}
                            label="Imagen de portada"
                            value={data.cover_image}
                            onChange={(v) =>
                                update({ ...data, cover_image: v })
                            }
                            buttonLabel="Subir foto desde el móvil"
                            hint="Se ve en la cabecera de tu invitación. Recomendamos foto horizontal."
                            aspect="16/10"
                        />
                    </div>
                </div>
            ),
        },
        {
            id: "ceremony",
            title: "Ceremonia",
            subtitle: "Lugar y dirección del enlace",
            render: () => (
                <div className="space-y-5">
                    <div>
                        <label className="label-soft">Nombre del lugar</label>
                        <input
                            data-testid={EDITOR.fieldCeremonyName}
                            type="text"
                            className="input-soft"
                            value={data.ceremony?.name || ""}
                            onChange={updateVenue("ceremony")("name")}
                            placeholder="Ej. Iglesia de San Miguel"
                        />
                    </div>
                    <div>
                        <label className="label-soft">Dirección</label>
                        <input
                            data-testid={EDITOR.fieldCeremonyAddress}
                            type="text"
                            className="input-soft"
                            value={data.ceremony?.address || ""}
                            onChange={updateVenue("ceremony")("address")}
                            placeholder="Calle, número, ciudad"
                        />
                    </div>
                    <div>
                        <label className="label-soft">Hora</label>
                        <input
                            data-testid={EDITOR.fieldCeremonyTime}
                            type="time"
                            className="input-soft"
                            value={data.ceremony?.time || ""}
                            onChange={updateVenue("ceremony")("time")}
                        />
                    </div>
                    <div>
                        <label className="label-soft">
                            Enlace de Google Maps (opcional)
                        </label>
                        <input
                            data-testid={EDITOR.fieldCeremonyMap}
                            type="url"
                            className="input-soft"
                            value={data.ceremony?.map_url || ""}
                            onChange={(e) => {
                                update({
                                    ...data,
                                    ceremony: {
                                        ...data.ceremony,
                                        map_url: e.target.value,
                                        map_url_manual: true,
                                    },
                                });
                            }}
                            placeholder="Se genera automáticamente"
                        />
                    </div>
                </div>
            ),
        },
        {
            id: "party",
            title: "Celebración",
            subtitle: "Lugar del banquete y fiesta",
            render: () => (
                <div className="space-y-5">
                    <div>
                        <label className="label-soft">Nombre del lugar</label>
                        <input
                            data-testid={EDITOR.fieldPartyName}
                            type="text"
                            className="input-soft"
                            value={data.party?.name || ""}
                            onChange={updateVenue("party")("name")}
                            placeholder="Ej. Finca El Olivar"
                        />
                    </div>
                    <div>
                        <label className="label-soft">Dirección</label>
                        <input
                            data-testid={EDITOR.fieldPartyAddress}
                            type="text"
                            className="input-soft"
                            value={data.party?.address || ""}
                            onChange={updateVenue("party")("address")}
                            placeholder="Carretera, km, ciudad"
                        />
                    </div>
                    <div>
                        <label className="label-soft">Hora</label>
                        <input
                            data-testid={EDITOR.fieldPartyTime}
                            type="time"
                            className="input-soft"
                            value={data.party?.time || ""}
                            onChange={updateVenue("party")("time")}
                        />
                    </div>
                    <div>
                        <label className="label-soft">
                            Enlace de Google Maps (opcional)
                        </label>
                        <input
                            data-testid={EDITOR.fieldPartyMap}
                            type="url"
                            className="input-soft"
                            value={data.party?.map_url || ""}
                            onChange={(e) => {
                                update({
                                    ...data,
                                    party: {
                                        ...data.party,
                                        map_url: e.target.value,
                                        map_url_manual: true,
                                    },
                                });
                            }}
                            placeholder="Se genera automáticamente"
                        />
                    </div>
                </div>
            ),
        },
        {
            id: "message",
            title: "Tu mensaje",
            subtitle: "Unas palabras para vuestros invitados",
            render: () => (
                <div className="space-y-4">
                    <SectionToggle
                        label="Mostrar mensaje en la web"
                        checked={data.messageEnabled ?? true}
                        onChange={() =>
                            update({
                                ...data,
                                messageEnabled: !(data.messageEnabled ?? true),
                            })
                        }
                        testId="editor-toggle-message"
                    />
                    <div>
                        <label className="label-soft">Mensaje</label>
                        <textarea
                            data-testid={EDITOR.fieldMessage}
                            className="input-soft"
                            rows={6}
                            value={data.message}
                            onChange={updateField("message")}
                            placeholder="Estamos deseando celebrarlo contigo..."
                        />
                        <AiImproveButton
                            data={data}
                            onImproved={(improved) =>
                                update({ ...data, message: improved })
                            }
                        />
                    </div>
                </div>
            ),
        },
    ];
}